'use client';

import { useState } from 'react';
import { Plus } from 'lucide-react';
import FadeInSection from '@/components/ui/FadeInSection';

export interface FaqItem {
  q: string;
  a: string;
}

// Reusable FAQ accordion — used by the home FaqSection and by blog articles.
// Emits FAQPage structured data mirroring the visible Q&A, so any page that
// renders it gets the rich-result markup for free. `idPrefix` keeps ARIA ids
// unique if a page ever hosts two accordions.
export default function FaqAccordion({
  items,
  idPrefix = 'faq',
}: {
  items: FaqItem[];
  idPrefix?: string;
}) {
  // Independent toggles — several answers can stay open while scanning.
  const [open, setOpen] = useState<Set<number>>(() => new Set());

  const toggle = (index: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <ul className="mt-stack-lg space-y-3">
        {items.map((item, i) => {
          const isOpen = open.has(i);
          const panelId = `${idPrefix}-panel-${i}`;
          const buttonId = `${idPrefix}-trigger-${i}`;

          return (
            <li key={item.q} className="forest-card overflow-hidden">
              <FadeInSection delay={i * 0.04}>
                <h3>
                  <button
                    type="button"
                    id={buttonId}
                    aria-controls={panelId}
                    aria-expanded={isOpen}
                    onClick={() => toggle(i)}
                    className="flex w-full items-center gap-4 px-6 py-5 text-left transition-colors duration-300 hover:bg-surface-bright/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary md:px-8 md:py-6"
                  >
                    <span
                      className={`flex-1 font-headline text-headline-md font-semibold transition-colors duration-300 ${
                        isOpen ? 'text-primary' : 'text-on-surface'
                      }`}
                    >
                      {item.q}
                    </span>
                    <span
                      aria-hidden="true"
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-primary/30 text-primary transition-colors duration-300"
                    >
                      <Plus
                        width={18}
                        height={18}
                        strokeWidth={1.75}
                        className={`transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}
                      />
                    </span>
                  </button>
                </h3>

                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-6 text-body-md text-on-surface-variant md:px-8 md:text-body-lg">
                      {item.a}
                    </p>
                  </div>
                </div>
              </FadeInSection>
            </li>
          );
        })}
      </ul>
    </>
  );
}
